import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconSettings,
} from '@u21/tabler-icons';
import {
  U21Button,
  U21MenuLayout,
  U21MenuList,
} from 'app/shared/u21-ui/components';

import { AppliedSavedFilterButton } from 'app/modules/filters/components/AppliedSavedFilterButton';
import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { ManageSavedFiltersDrawer } from 'app/modules/filters/components/ManageSavedFiltersDrawer';
import { SavedFilterButton } from 'app/modules/filters/components/SavedFilterButton';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import { orderSavedFilters } from 'app/modules/filters/utils';
import { SAVED_FILTERS_ORDER_KEYS } from 'app/modules/filters/constants';

interface Props {
  appliedSavedFilter: SavedFilter | undefined;
  defaultFilters: Filter[];
  filterContext: FilterContext;
  filters: Filter[];
  isDirty: (savedFilter: SavedFilter) => boolean;
  onApplySavedFilter: (savedFilter: SavedFilter) => void;
  onAppliedSavedFilterChange: Dispatch<SetStateAction<SavedFilter | undefined>>;
  onChange: (filters: Filter[]) => void;
  savedFilters: SavedFilter[];
  staleSavedFilterIds: Set<number>;
}

export const SavedFilterBar = ({
  appliedSavedFilter,
  defaultFilters,
  filterContext,
  filters,
  isDirty,
  onApplySavedFilter,
  onAppliedSavedFilterChange,
  onChange,
  savedFilters,
  staleSavedFilterIds,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureChipRefs = useRef<Array<HTMLDivElement | null>>([]);
  const measureOverflowTriggerRef = useRef<HTMLDivElement | null>(null);
  const measureGearRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(savedFilters.length);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const [storedOrder, setStoredOrder] = useLocalStorageState<number[]>(
    SAVED_FILTERS_ORDER_KEYS[filterContext],
    [],
  );

  const orderedSavedFilters = useMemo(
    () => orderSavedFilters(savedFilters, storedOrder),
    [savedFilters, storedOrder],
  );

  // measure widths and decide how many chips fit on a single line
  const updateVisibleCount = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const available = container.clientWidth;
    if (available === 0) {
      setVisibleCount(savedFilters.length);
      return;
    }
    const overflowTriggerWidth =
      measureOverflowTriggerRef.current?.offsetWidth ?? 0;
    const gearWidth = measureGearRef.current?.offsetWidth ?? 0;
    // gear button is always rendered, so always reserve its width
    const baseAvailable = available - gearWidth;
    // truncate stale trailing slots from prior renders so length matches current chips
    const chipCount = savedFilters.length;
    measureChipRefs.current.length = chipCount;
    let used = 0;
    let count = 0;
    for (let i = 0; i < chipCount; i += 1) {
      const node = measureChipRefs.current[i];
      if (!node) {
        continue;
      }
      const childWidth = node.offsetWidth;
      const needsOverflowButton = i < chipCount - 1;
      const budget = needsOverflowButton
        ? baseAvailable - overflowTriggerWidth
        : baseAvailable;
      if (used + childWidth > budget) {
        break;
      }
      used += childWidth;
      count += 1;
    }
    setVisibleCount(count);
  }, [savedFilters.length]);

  const updateVisibleCountRef = useRef(updateVisibleCount);
  useLayoutEffect(() => {
    updateVisibleCountRef.current = updateVisibleCount;
  }, [updateVisibleCount]);

  useLayoutEffect(() => {
    updateVisibleCountRef.current();
  }, [savedFilters, appliedSavedFilter, filters, orderedSavedFilters]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    // wrapper indirects through ref so observer always calls the latest callback
    const observer = new ResizeObserver(() => updateVisibleCountRef.current());
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // hoist applied filter to front only if it would otherwise be in overflow —
  // keeps the applied chip + its action menu always reachable in the bar
  const displaySavedFilters = useMemo(() => {
    if (!appliedSavedFilter) {
      return orderedSavedFilters;
    }
    const naturalIndex = orderedSavedFilters.findIndex(
      (i) => i.id === appliedSavedFilter.id,
    );
    if (naturalIndex === -1 || naturalIndex < visibleCount) {
      return orderedSavedFilters;
    }
    return [
      appliedSavedFilter,
      ...orderedSavedFilters.filter((i) => i.id !== appliedSavedFilter.id),
    ];
  }, [appliedSavedFilter, orderedSavedFilters, visibleCount]);

  const overflowSavedFiltersOptions = useMemo(
    () =>
      displaySavedFilters.slice(visibleCount).map((i) => {
        const isStale = staleSavedFilterIds.has(i.id);
        return {
          icon: isStale ? <StaleIcon /> : undefined,
          rightIcon:
            appliedSavedFilter?.id === i.id ? <StyledIconCheck /> : undefined,
          text: i.title,
          tooltip: isStale
            ? 'Some filters in this saved filter may be unavailable.'
            : undefined,
          onClick: () => {
            onApplySavedFilter(i);
            setOverflowMenuOpen(false);
          },
        };
      }),
    [
      appliedSavedFilter,
      displaySavedFilters,
      onApplySavedFilter,
      staleSavedFilterIds,
      visibleCount,
    ],
  );

  const overflowButtonLabel = `+${overflowSavedFiltersOptions.length} more`;

  return (
    <Container ref={containerRef}>
      {/* off-screen measure layer with all chips + worst-case trigger visible */}
      <MeasureLayer aria-hidden="true">
        {displaySavedFilters.map((i, index) => {
          const isApplied = appliedSavedFilter?.id === i.id;
          const isStale = staleSavedFilterIds.has(i.id);
          return (
            <ChipWrapper
              key={i.id}
              ref={(node) => {
                measureChipRefs.current[index] = node;
              }}
              aria-hidden="true"
            >
              {isApplied ? (
                <AppliedSavedFilterButton
                  defaultFilters={defaultFilters}
                  dirty={isDirty(i)}
                  filters={filters}
                  filterContext={filterContext}
                  isStale={isStale}
                  onAppliedSavedFilterChange={onAppliedSavedFilterChange}
                  onFiltersChange={onChange}
                  savedFilter={i}
                />
              ) : (
                <SavedFilterButton
                  disableTitleCase
                  startIcon={isStale ? <StaleIcon /> : undefined}
                >
                  {i.title}
                </SavedFilterButton>
              )}
            </ChipWrapper>
          );
        })}
        <ChipWrapper ref={measureOverflowTriggerRef} aria-hidden="true">
          <SavedFilterButton
            disableTitleCase
            endIcon={overflowMenuOpen ? <IconChevronUp /> : <IconChevronDown />}
          >
            {overflowButtonLabel}
          </SavedFilterButton>
        </ChipWrapper>
        <ChipWrapper ref={measureGearRef} aria-hidden="true">
          <U21Button aria-label="hidden" icon={<IconSettings />} size="small" />
        </ChipWrapper>
      </MeasureLayer>

      <ChipRow>
        {displaySavedFilters.map((i, index) => {
          const isApplied = appliedSavedFilter?.id === i.id;
          const isStale = staleSavedFilterIds.has(i.id);
          const hidden = index >= visibleCount;
          if (isApplied) {
            return (
              <ChipWrapper key={i.id} $hidden={hidden}>
                <AppliedSavedFilterButton
                  defaultFilters={defaultFilters}
                  dirty={isDirty(i)}
                  filters={filters}
                  filterContext={filterContext}
                  isStale={isStale}
                  onAppliedSavedFilterChange={onAppliedSavedFilterChange}
                  onFiltersChange={onChange}
                  savedFilter={i}
                />
              </ChipWrapper>
            );
          }
          return (
            <ChipWrapper key={i.id} $hidden={hidden}>
              <SavedFilterButton
                disableTitleCase
                onClick={() => onApplySavedFilter(i)}
                startIcon={isStale ? <StaleIcon /> : undefined}
                tooltip={
                  isStale
                    ? 'Some filters in this saved filter may be unavailable.'
                    : undefined
                }
              >
                {i.title}
              </SavedFilterButton>
            </ChipWrapper>
          );
        })}
        {overflowSavedFiltersOptions.length > 0 && (
          <U21MenuLayout
            alignRight
            onClose={() => setOverflowMenuOpen(false)}
            open={overflowMenuOpen}
            trigger={
              <SavedFilterButton
                aria-label="More saved filters"
                disableTitleCase
                endIcon={
                  overflowMenuOpen ? <IconChevronUp /> : <IconChevronDown />
                }
                onClick={() => setOverflowMenuOpen(true)}
              >
                {overflowButtonLabel}
              </SavedFilterButton>
            }
          >
            <U21MenuList
              options={overflowSavedFiltersOptions}
              placeholder="Search saved filters"
            />
          </U21MenuLayout>
        )}
        <GearSpacer />
        <U21Button
          aria-label="Manage saved filters"
          icon={<IconSettings />}
          onClick={() => setManageOpen(true)}
          size="small"
        />
      </ChipRow>
      <ManageSavedFiltersDrawer
        filterContext={filterContext}
        onClose={() => setManageOpen(false)}
        onReorder={setStoredOrder}
        open={manageOpen}
        orderedSavedFilters={orderedSavedFilters}
        staleSavedFilterIds={staleSavedFilterIds}
      />
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;
`;

const ChipRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
`;

const GearSpacer = styled.div`
  flex: 1;
`;

const ChipWrapper = styled.div<{ $hidden?: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'inline-flex')};
`;

const StaleIcon = styled(IconAlertTriangle)`
  color: ${(props) => props.theme.palette.warning.main};
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${(props) => props.theme.palette.primary.main};
`;

const MeasureLayer = styled.div`
  display: flex;
  left: -9999px;
  pointer-events: none;
  position: absolute;
  top: 0;
  visibility: hidden;
  white-space: nowrap;

  // strip from accessible tree + tab order
  & button {
    pointer-events: none;
  }
`;
